/**
 * Case Checklist Component
 * ========================
 * Displays and manages the investigation checklist for a case.
 * Shows progress, allows marking items complete/incomplete.
 */

import React from 'react';
import {
  CheckCircle2,
  Circle,
  Loader2,
  ListChecks,
  AlertTriangle,
  User,
} from 'lucide-react';
import {
  useCaseChecklist,
  useCompleteChecklistItem,
  useUncompleteChecklistItem,
} from '../../hooks/usePlaybooks';
import { useToast } from '../../contexts/ToastContext';

const formatRelativeTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

export default function CaseChecklist({ caseId, showWarning = true }) {
  const toast = useToast();
  const { data: checklist, isLoading, error } = useCaseChecklist(caseId);
  const completeItem = useCompleteChecklistItem();
  const uncompleteItem = useUncompleteChecklistItem();

  const handleToggleItem = async (item) => {
    try {
      if (item.completed) {
        await uncompleteItem.mutateAsync({ caseId, itemIndex: item.index });
        toast.success('Item uncompleted');
      } else {
        await completeItem.mutateAsync({ caseId, itemIndex: item.index });
        toast.success('Item completed');
      }
    } catch (err) {
      toast.error('Failed to update checklist');
    }
  };

  if (isLoading) {
    return (
      <div style={styles.loading}>
        <Loader2 size={20} style={styles.spinner} />
        <span>Loading checklist...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.error}>
        <AlertTriangle size={16} />
        <span>Failed to load checklist</span>
      </div>
    );
  }

  // No checklist for this case
  if (!checklist || checklist.total_items === 0) {
    return (
      <div style={styles.empty}>
        <ListChecks size={24} style={{ opacity: 0.5 }} />
        <span>No checklist for this case</span>
      </div>
    );
  }

  const progress = checklist.total_items > 0
    ? Math.round((checklist.completed_count / checklist.total_items) * 100)
    : 0;

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerTitle}>
          <ListChecks size={16} />
          <span>Checklist</span>
        </div>
        <div style={styles.progress}>
          {checklist.completed_count}/{checklist.total_items}
        </div>
      </div>

      {/* Progress Bar */}
      <div style={styles.progressBar}>
        <div
          style={{
            ...styles.progressFill,
            width: `${progress}%`,
            backgroundColor: checklist.all_completed ? 'var(--color-success)' : 'var(--color-primary)',
          }}
        />
      </div>

      {/* Playbook Name */}
      {checklist.playbook_name && (
        <div style={styles.playbookName}>
          {checklist.playbook_name}
        </div>
      )}

      {/* Items */}
      <div style={styles.items}>
        {checklist.items.map((item) => (
          <div
            key={item.index}
            style={{
              ...styles.item,
              ...(item.completed ? styles.itemCompleted : {}),
            }}
          >
            <button
              style={styles.checkbox}
              onClick={() => handleToggleItem(item)}
              disabled={completeItem.isPending || uncompleteItem.isPending}
            >
              {item.completed ? (
                <CheckCircle2 size={18} style={{ color: 'var(--color-success)' }} />
              ) : (
                <Circle size={18} style={{ color: 'var(--text-secondary)' }} />
              )}
            </button>
            <div style={styles.itemContent}>
              <div style={{
                ...styles.itemLabel,
                ...(item.completed ? styles.itemLabelCompleted : {}),
              }}>
                {item.label}
              </div>
              {item.description && (
                <div style={styles.itemDescription}>{item.description}</div>
              )}
              {item.completed && item.completed_by_name && (
                <div style={styles.completionInfo}>
                  <User size={12} />
                  <span>{item.completed_by_name}</span>
                  <span>•</span>
                  <span>{formatRelativeTime(item.completed_at)}</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Warning if checklist is required but incomplete */}
      {showWarning && checklist.is_blocking && !checklist.all_completed && (
        <div style={styles.warning}>
          <AlertTriangle size={14} />
          <span>Complete checklist before resolving case</span>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    background: 'var(--bg-primary)',
    border: '1px solid var(--border-color)',
    borderRadius: '12px',
    padding: '16px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: 600,
    color: 'var(--text-primary)',
  },
  progress: {
    fontSize: '13px',
    fontWeight: 500,
    color: 'var(--text-secondary)',
  },
  progressBar: {
    height: '4px',
    background: 'var(--bg-tertiary)',
    borderRadius: '2px',
    overflow: 'hidden',
    marginBottom: '12px',
  },
  progressFill: {
    height: '100%',
    borderRadius: '2px',
    transition: 'width 0.3s ease',
  },
  playbookName: {
    fontSize: '12px',
    color: 'var(--text-muted)',
    marginBottom: '12px',
  },
  items: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  item: {
    display: 'flex',
    gap: '12px',
    padding: '12px',
    background: 'var(--bg-secondary)',
    borderRadius: '8px',
    transition: 'all 0.15s',
  },
  itemCompleted: {
    opacity: 0.7,
  },
  checkbox: {
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'flex-start',
    paddingTop: '2px',
  },
  itemContent: {
    flex: 1,
    minWidth: 0,
  },
  itemLabel: {
    fontSize: '14px',
    fontWeight: 500,
    color: 'var(--text-primary)',
    marginBottom: '4px',
  },
  itemLabelCompleted: {
    textDecoration: 'line-through',
    color: 'var(--text-secondary)',
  },
  itemDescription: {
    fontSize: '12px',
    color: 'var(--text-muted)',
    lineHeight: 1.4,
  },
  completionInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginTop: '8px',
    fontSize: '11px',
    color: 'var(--color-success)',
  },
  warning: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: '12px',
    padding: '10px 12px',
    background: 'var(--color-warning-bg)',
    color: 'var(--color-warning)',
    borderRadius: '8px',
    fontSize: '13px',
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '16px',
    color: 'var(--text-secondary)',
    fontSize: '13px',
  },
  spinner: {
    animation: 'spin 1s linear infinite',
  },
  error: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '16px',
    color: 'var(--color-danger)',
    fontSize: '13px',
  },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    padding: '24px',
    color: 'var(--text-muted)',
    fontSize: '13px',
  },
};
